import type { ChannelSlice } from '../ChannelSlice';
import type { WidgetTabSlice } from '../WidgetTabSlice';
import type { JsonSchema, Layout } from '@jsonforms/core';
import { effect, signal, computed } from '@preact/signals-react';
import type { Signal, ReadonlySignal } from '@preact/signals-react';
import { QueryObserver } from '@tanstack/query-core';
import type { QueryObserverResult } from '@tanstack/query-core';
import { Slugs } from '@thinkalpha/common/constants/slugs.js';
import type { Instrument } from '@thinkalpha/common/schemas/instruments.js';
import { OrderType } from '@thinkalpha/trading-ws-client/contracts/order.js';
import type { OpenClose, Order } from '@thinkalpha/trading-ws-client/contracts/order.js';
import type { TradeProWidgetModel as TradeProWidgetModelContract } from '@thinkalpha/ui-types/contracts/workspace';
import type { InstrumentFormState } from '@thinkalpha/ui-types/types/instrument';
import type { ErrorObject } from 'ajv';
import { t } from 'i18next';
import { optional } from 'inversify';
import { from } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { generateAccountNameToDisplay } from 'src/components/menus/AccountsMenu/utils';
import type { ConversionResult } from 'src/features/csv-upload/state/jsonUtils';
import { ReactiveInjectable, reacts, inject, injectable } from 'src/features/ioc';
import type { InstrumentType } from 'src/features/symbol-picker/state';
import type { SymbolPickerAction } from 'src/features/symbol-picker/state/actions';
import { createInitialState as createSymbolPickerInitialState } from 'src/features/symbol-picker/state/utils';
import { enableCsvOrderUpload } from 'src/lib/featureFlags/flags/enableCsvOrderUpload';
import { instrumentToString, parseInstrument, parseInstrumentToFormState } from 'src/lib/instrumentUtils';
import { extractSchemaPropertyOptions } from 'src/lib/jsonSchema/extractSchemaPropertyOptions';
import { removeQueuedPropsFromOrderData } from 'src/lib/orders/removePropsFromOrderData';
import type { TableModel } from 'src/models/TableModel';
import { DEFAULT_EXDESTINATION, DEFAULT_ROUTE } from 'src/models/TradeProWidgetModel';
import type { TradeProWidgetModel } from 'src/models/TradeProWidgetModel';
import type { WidgetHeaderElement } from 'src/models/WidgetModel';
import { COMMON_HEADER_ELEMENTS } from 'src/models/WidgetModel/impl';
import { getTickersForSymbolQuery } from 'src/queries/table';
import { getExecutorsJsonSchemaQuery } from 'src/queries/trading-ws/schemas';
import type { SendOrderData } from 'src/routes/widgets/TradeProWidget/api';
import { MultiLegMarketData } from 'src/routes/widgets/TradeProWidget/lib/MultiLegMarketData';
import {
    createLeg,
    setSelectedAccountEffectiveAccess,
    setExDestinationAndRouteFromBaseSchemaDefaults,
    setPriceByClickingATicker,
    setShowTradeToast,
    setSkipTradeConfirmation,
    setInstrument,
    tradeProReducer,
    initialState,
    patchOrderData,
    setOrderErrors,
    setMarketData,
    resetOrderData,
    initializeState,
    addLeg,
    updateLeg,
    removeLeg,
    setActiveStrategy,
    updateLegSymbolPicker,
    startFetchingExecutorSchema,
    failedFetchingExecutorSchema,
    formRenderFailed,
    MAX_FORM_RENDER_RETRIES,
    fetchedExecutorSchema,
    OptionLeg,
} from 'src/routes/widgets/TradeProWidget/state';
import type {
    MarketData,
    TradeProState,
    TradeProAction,
    OrderData,
    BaseSchema,
    PriceTicker,
    Leg,
} from 'src/routes/widgets/TradeProWidget/state';
import { OrderAction } from 'src/routes/widgets/TradeProWidget/state/types';
import { isMultiLegState } from 'src/routes/widgets/TradeProWidget/state/utils';
import type { AccountService } from 'src/services/AccountService';
import { AccountServiceImpl } from 'src/services/AccountService/impl';
import type { MarketDataService } from 'src/services/MarketDataService';
import type { MessagesService } from 'src/services/MessagesService';
import type { TradingSocketService, TradingSocketConnectionStatus } from 'src/services/TradingSocketService';
import type { WorkspaceService } from 'src/services/WorkspaceService';
import { container } from 'src/StaticContainer';
import { updateWidget } from 'src/store/actions/widgetAndChannel';
import { updateTradeProInstrument, userSelectedTradeProAccount } from 'src/store/actions/widgets/tradePro';
import type { ReactBindings } from 'src/types/bindings';
import { EventBusBuilder, MergedEventBus } from 'src/types/EventBus/EventBus';

const log = container.get('Logger').getSubLogger({ name: 'TradeProWidgetModelImpl' }, { component: 'trading' });

/**
 * Validates that an instrument has a valid symbol.
 * Returns true if the instrument has a non-empty symbol (for equity) or
 * a non-empty underlying symbol (for options).
 */
function validateInstrument(instrument: Instrument | undefined): instrument is Instrument {
    if (!instrument) return false;
    if (instrument.type === 'equity') {
        return !!instrument.symbol;
    }
    if (instrument.type === 'option') {
        return !!instrument.underlyingInstrument?.symbol;
    }
    return false;
}

@injectable()
export class TradeProWidgetModelImpl extends ReactiveInjectable implements TradeProWidgetModel {
    #channelSlice: ChannelSlice;
    #widgetTabSlice: WidgetTabSlice;

    #defaultInstrument: Instrument = {
        type: 'equity',
        symbol: 'AAPL',
    };

    #state: Signal<TradeProState> = signal(structuredClone(initialState));

    #orderData: ReadonlySignal<OrderData> = computed(() => this.#state.value.orderData);

    #executorSchemaObserver!: QueryObserver<BaseSchema>;

    #underlyingSymbol: ReadonlySignal<string> = computed<string>(() =>
        this.#orderData.value.instrument?.type === 'equity'
            ? this.#orderData.value.instrument?.symbol
            : this.#orderData.value.instrument?.type === 'option' &&
                this.#orderData.value.instrument?.underlyingInstrument.type === 'equity'
              ? this.#orderData.value.instrument?.underlyingInstrument.symbol
              : '',
    );

    #marketDataTable: TableModel;

    #accountService: AccountService;

    isMultiLegWidget!: boolean;

    // Multi-leg specific computed signals
    #legs: ReadonlySignal<Leg[]> = computed(() => {
        const state = this.#state.value;
        return isMultiLegState(state) ? state.orderData.legs : [];
    });

    #activeStrategy: ReadonlySignal<string | undefined> = computed(() => {
        const state = this.#state.value;
        return isMultiLegState(state) ? state.activeStrategy : undefined;
    });

    #multiLegMarketData: MultiLegMarketData | null = null;

    #marketDataService: MarketDataService;

    #csvUploadEnabled = signal(false);

    // Widget header elements - common elements (excluding widgetName for space) plus TradePro-specific ones
    headerElements: WidgetHeaderElement[] = [
        ...COMMON_HEADER_ELEMENTS.filter((el) => el.type !== 'widgetName'), // TradePro didn't show name element so far so we are removing it
        { type: 'symbolPicker', position: 25, priority: 80 },
        {
            type: 'accounts',
            position: 30,
            priority: 70,
            selectType: 'single',
            disabledAccountIdentifiers: { canWrite: true },
        },
        { type: 'tradeProSettingsMenu', position: 81, priority: 50 },
        { type: 'tradeProCsvUpload', position: 82, priority: 60 },
        { type: 'connectionStatus', position: 995, priority: 995, overflowPosition: 5 },
    ];

    // EventBus for widget header communication
    eventBus!: TradeProWidgetModel['eventBus'];

    #buildEventBus(): TradeProWidgetModel['eventBus'] {
        return MergedEventBus.from(
            EventBusBuilder.create()
                // TradePro-specific events
                .addEvent('widget::instrumentChange', (payload) => {
                    this.setInstrument(parseInstrument(payload.instrument));
                })
                // Account events
                .addEvent('widget::updateSelectedAccountId', (payload) => {
                    this.setOrderData({ account: payload.accountId });
                })
                .addEvent('widget::setIsAccountSelectionValid', (_payload) => {
                    // TradePro doesn't track account selection validity separately
                })
                // TradePro settings events
                .addEvent('widget::tradeProToggleShowTradeToast', () => {
                    this.setShowTradeToast(!this.#showTradeToast.value);
                })
                .addEvent('widget::tradeProToggleSkipTradeConfirmation', () => {
                    this.setSkipTradeConfirmation(!this.#skipTradeConfirmation.value);
                })
                .addEvent('widget::tradeProCsvUploadSubmit', (payload) => {
                    this.handleCsvUpload(payload.conversionResult);
                })
                // TradePro-specific signals
                .addSignal('widget::instrument', () =>
                    computed(() => instrumentToString(this.#orderData.value.instrument ?? this.#defaultInstrument)),
                )
                .addSignal('widget::allowedInstrumentTypes', () => this.#allowedInstrumentTypes)
                .addSignal('widget::connectionStatus', () =>
                    computed(() => ({
                        status: this.tradingSocketConnectionStatus.value,
                        progress: undefined,
                        failureCount: 0,
                    })),
                )
                // Account signals
                .addSignal('widget::selectedAccountId', () => computed(() => this.#orderData.value.account || null))
                // TradePro settings signals
                .addSignal('widget::tradeProShowTradeToast', () => this.#showTradeToast)
                .addSignal('widget::tradeProSkipTradeConfirmation', () => this.#skipTradeConfirmation)
                .addSignal('widget::tradeProCsvUploadEnabled', () => this.#csvUploadEnabled)
                // Instrument type filtering signal
                .build(),
        )
            .merge(this.#channelSlice.eventBus)
            .merge(this.#widgetTabSlice.eventBus);
    }

    constructor(
        @inject('WidgetDataModel') @reacts private widgetData: ReactBindings['WidgetDataModel'],
        @inject('Store') private store: ReactBindings['Store'],
        @inject('QueryClient') private queryClient: ReactBindings['QueryClient'],
        @inject('TableModel') marketDataTable: TableModel,
        @inject('MarketDataService') marketDataService: MarketDataService,
        @inject('TradingSocketService') private tradingSocketService: TradingSocketService,
        @inject('ChannelSlice') channelSlice: ChannelSlice,
        @inject('WidgetTabSlice') widgetTabSlice: WidgetTabSlice,
        @inject('WorkspaceService') @optional() workspaceService?: WorkspaceService,
        @inject('TabId') @optional() tabId?: string,
        @inject('MessagesService') @optional() private readonly messagesService?: MessagesService,
    ) {
        // eslint-disable-next-line prefer-rest-params -- forwarding constructor args
        super(...arguments);
        this.#marketDataTable = marketDataTable;
        this.#accountService = new AccountServiceImpl(queryClient);
        this.#marketDataService = marketDataService;

        this.#channelSlice = channelSlice;
        this.#widgetTabSlice = widgetTabSlice;

        // Initialize CSV upload feature flag
        this.#csvUploadEnabled.value = enableCsvOrderUpload.select(store.getState());

        // Subscribe to feature flag changes
        this.disposableStack.defer(
            store
                .subscribeSelector((state) => enableCsvOrderUpload.select(state))
                .handle((flagEnabled) => {
                    this.#csvUploadEnabled.value = flagEnabled;
                }),
        );

        if (tabId && workspaceService) {
            workspaceService.registerWidget(tabId, this);
            this.disposableStack.defer(() => workspaceService.deregisterWidget(tabId));
        }

        this.eventBus = this.#buildEventBus();
    }

    async init(tabId: string) {
        this.widgetData.init(tabId);

        // Initialize slices
        this.#channelSlice.init(tabId);
        this.#widgetTabSlice.init(tabId);
        this.disposableStack.use(this.#channelSlice);
        this.disposableStack.use(this.#widgetTabSlice);

        // Initialize with correct state type
        const stateType = this.widget.type === 'trade-pro-multi' ? 'multi-leg' : 'single-leg';
        this.isMultiLegWidget = this.widget.type === 'trade-pro-multi';
        const initialInstrument = validateInstrument(this.widget.instrument)
            ? this.widget.instrument
            : { ...this.#defaultInstrument };
        // Track whether we used the default so we can persist it to Redux
        const usedDefaultInstrument = !validateInstrument(this.widget.instrument);

        this.dispatch(
            initializeState({
                stateType,
                showTradeToast: this.widget.showTradeToast ?? true,
                skipTradeConfirmation: this.widget.skipTradeConfirmation ?? false,
                instrument: initialInstrument,
                account: this.widget.accountId ?? '',
            }),
        );

        if (tabId) {
            this.disposableStack.defer(
                effect(() => {
                    this.messagesService?.setTradingToastsEnabled(tabId, this.#showTradeToast.value);
                }),
            );
            this.disposableStack.defer(() => {
                this.messagesService?.clearTradingToastsEnabled(tabId);
            });
        }

        // If we defaulted to AAPL because the saved instrument was invalid,
        // persist the default to Redux so it gets saved with the workspace
        if (usedDefaultInstrument) {
            this.store.dispatch(updateTradeProInstrument(tabId, initialInstrument));
        }

        // Subscribe to trading socket
        this.disposableStack.defer(
            effect(() => {
                const accountId = this.#orderData.value.account;

                if (accountId) {
                    return this.tradingSocketService.subscribeToAccounts([accountId]);
                }
            }),
        );

        // init multi-leg market data
        if (this.isMultiLegWidget) {
            this.#multiLegMarketData = new MultiLegMarketData(this.#marketDataService, this.#legs);
            this.disposableStack.use(this.#multiLegMarketData);
        }

        // init market data
        {
            const queryOptions = getTickersForSymbolQuery(instrumentToString(initialInstrument));
            this.#marketDataTable.init({
                queryOptions,
                initBounds: { firstRow: 0, windowSize: 1 },
                initThrottle: 1,
                devtoolsName: 'TradePro/marketData',
            });
        }

        // load accounts
        {
            this.#accountService.loadAccounts();

            this.disposableStack.defer(
                effect(() => {
                    const accounts = (this.#accountService?.accounts || []).toSorted((a, b) =>
                        generateAccountNameToDisplay(a).localeCompare(generateAccountNameToDisplay(b)),
                    );

                    // Set the first available account
                    if (!this.#orderData.value.account && accounts.length > 0) {
                        this.dispatch(patchOrderData({ account: accounts[0].id }));
                        this.dispatch(
                            setSelectedAccountEffectiveAccess(accounts[0]?.permissions.effectiveAccess ?? 'reader'),
                        );
                    }
                }),
            );
        }

        // update market data
        {
            this.disposableStack.defer(
                effect(() => {
                    if (this.#marketDataTable.connectionStatus.value !== 'connected') {
                        return;
                    }
                    const updateData = this.marketDataTable.updateSignal.value?.update;

                    if (!updateData) {
                        return;
                    }

                    const columns = this.marketDataTable.columnsSignal.value;
                    const columnBid = columns.find((column) => column.matchSlug === Slugs.bid);
                    const columnAsk = columns.find((column) => column.matchSlug === Slugs.ask);
                    const columnLast = columns.find((column) => column.matchSlug === Slugs.last);

                    const bid = columnBid ? (updateData[columnBid.id] as number | undefined) : undefined;
                    const ask = columnAsk ? (updateData[columnAsk.id] as number | undefined) : undefined;
                    const last = columnLast ? (updateData[columnLast.id] as number | undefined) : undefined;

                    this.dispatch(setMarketData({ bid, ask, last }));
                }),
            );
        }

        // init executor schema observer
        {
            const account = this.#orderData.value.account;
            const queryOptions = getExecutorsJsonSchemaQuery(account);

            this.#executorSchemaObserver = new QueryObserver<BaseSchema>(this.queryClient, queryOptions);

            const handleResult = (result: QueryObserverResult<BaseSchema>) => {
                if (result.isLoading) {
                    this.dispatch(startFetchingExecutorSchema());
                } else if (result.isError) {
                    log.error('Failed to fetch executors schema', { error: result.error });
                    this.dispatch(failedFetchingExecutorSchema(result.error as Error));
                } else if (result.isSuccess && result.data) {
                    // Pass account ID for schema filtering (see PLAT-11238)
                    const accountId = this.#orderData.value.account;
                    this.dispatch(fetchedExecutorSchema(result.data, accountId));

                    if (TRACE) {
                        log.trace('Fetched executor schema, after processing', { state: this.#state.value });
                    }
                }
            };

            handleResult(this.#executorSchemaObserver.getCurrentResult());
            const unsubscribe = this.#executorSchemaObserver.subscribe(handleResult);

            this.disposableStack.defer(() => unsubscribe());
            this.disposableStack.defer(() => this.#executorSchemaObserver.destroy());

            // Update query options and effective account access when account changes
            this.disposableStack.defer(
                effect(() => {
                    const account = this.#orderData.value.account;

                    this.dispatch(
                        setSelectedAccountEffectiveAccess(
                            (this.#accountService?.accounts || []).find((a) => a.id === account)?.permissions
                                .effectiveAccess ?? 'reader',
                        ),
                    );

                    const queryOptions = getExecutorsJsonSchemaQuery(account);
                    this.#executorSchemaObserver.setOptions(queryOptions);
                }),
            );
        }

        // watch for instrument and symbol changes from store
        {
            this.disposableStack.use(
                from(this.widgetData)
                    .pipe(
                        map(() => this.widget.instrument ?? null),
                        distinctUntilChanged(),
                    )
                    .subscribe((instrument) => {
                        // Do nothing if the instrument is just a placeholder
                        if (
                            !instrument ||
                            (instrument.type === 'equity' && !instrument.symbol) ||
                            (instrument.type === 'option' &&
                                instrument.underlyingInstrument?.type === 'equity' &&
                                !instrument.underlyingInstrument?.symbol)
                        ) {
                            return;
                        }

                        this.dispatch(setInstrument(instrument, this.widget.quantity));
                        this.updateMarketDataTable(instrumentToString(instrument));
                    }),
            );
        }

        // watch for account id changes from store
        {
            this.disposableStack.use(
                from(this.widgetData)
                    .pipe(
                        map(() => this.widget.accountId ?? ''),
                        distinctUntilChanged(),
                    )
                    .subscribe((accountId) => {
                        if (!accountId) {
                            return;
                        }
                        if (this.#orderData.value.account !== accountId) {
                            this.setOrderData({ account: accountId }, { fromStoreSync: true });
                        }
                    }),
            );
        }
    }

    #isReady = computed(() => {
        const accountId = this.#orderData.value.account;
        return (
            this.tradingSocketService.isConnected.value &&
            this.tradingSocketService.isAuthenticated.value &&
            (accountId ? this.tradingSocketService.joinedAccounts.value.has(accountId) : false)
        );
    });
    get isReady(): ReadonlySignal<boolean> {
        return this.#isReady;
    }

    #tradingSocketConnectionStatus = computed(() => {
        return this.tradingSocketService.isConnected.value && this.tradingSocketService.isAuthenticated.value
            ? 'connected'
            : this.tradingSocketService.isConnected.value
              ? 'connecting'
              : 'disconnected';
    });
    get tradingSocketConnectionStatus(): ReadonlySignal<TradingSocketConnectionStatus> {
        return this.#tradingSocketConnectionStatus;
    }

    #hasValidInstrument = computed(() => validateInstrument(this.#orderData.value.instrument));

    #canSubmit = computed(() => {
        const hasAccount = !!this.#orderData.value.account;
        const hasInstrument = this.#hasValidInstrument.value;
        const hasLegs = this.isMultiLegWidget ? this.legs.value.length > 0 : true;
        const hasOptionLeg = this.isMultiLegWidget
            ? this.legs.value.find((leg) => leg instanceof OptionLeg) !== undefined
            : true;

        const allLegsComplete = this.isMultiLegWidget
            ? this.legs.value.every((leg) => leg.symbolPickerState.symbolString.length > 0)
            : true;

        const allOptionLegsHaveOpenClose = this.isMultiLegWidget
            ? this.legs.value.every((leg) => {
                  if (leg.symbolPickerState.instrument.instrumentType !== 'options') {
                      return true;
                  }
                  return (leg as OptionLeg).openClose !== undefined;
              })
            : true;

        return (
            this.#isReady.value &&
            hasAccount &&
            hasInstrument &&
            this.#orderErrors.value.length === 0 &&
            hasLegs &&
            hasOptionLeg &&
            allLegsComplete &&
            allOptionLegsHaveOpenClose
        );
    });
    get canSubmit(): ReadonlySignal<boolean> {
        return this.#canSubmit;
    }

    #canSubmitError = computed((): string | null => {
        if (!this.#orderData.value.account) {
            return t('trade-pro::submit-errors::no-account-selected');
        }

        if (!this.#isReady.value) {
            return t('trade-pro::submit-errors::not-connected');
        }

        if (!this.#hasValidInstrument.value) {
            return t('trade-pro::submit-errors::missing-instrument-selection');
        }

        if (this.isMultiLegWidget) {
            if (this.legs.value.length === 0) {
                return t('trade-pro::submit-errors::at-least-one-leg-required');
            }

            const hasOptionLeg = this.legs.value.find((leg) => leg instanceof OptionLeg) !== undefined;
            if (!hasOptionLeg) {
                return t('trade-pro::submit-errors::at-least-one-option-leg-required');
            }

            const incompleteLegs = this.legs.value.some((leg) => leg.symbolPickerState.symbolString.length === 0);
            if (incompleteLegs) {
                return t('trade-pro::submit-errors::incomplete-leg-instruments');
            }

            const missingOpenClose = this.legs.value.some((leg) => {
                if (leg.symbolPickerState.instrument.instrumentType !== 'options') {
                    return false;
                }
                return (leg as OptionLeg).openClose === undefined;
            });
            if (missingOpenClose) {
                return t('trade-pro::submit-errors::all-option-legs-must-have-open-close');
            }
        }

        if (this.#orderErrors.value.length > 0) {
            return t('trade-pro::submit-errors::validation-errors');
        }

        return null;
    });
    get canSubmitError(): ReadonlySignal<string | null> {
        return this.#canSubmitError;
    }

    resetOrderData() {
        const account = this.#state.value.orderData.account;
        const instrument = this.#state.value.orderData.instrument;
        this.dispatch(resetOrderData({ account, instrument }));
        this.dispatch(setExDestinationAndRouteFromBaseSchemaDefaults());
    }

    canRetryFormRender = computed(() => this.#state.value.formRenderFailureCount < MAX_FORM_RENDER_RETRIES);

    reportFormRenderFailure(error: Error) {
        const { fallbackToBasicSchema, formRenderFailureCount } = this.#state.value;

        if (!fallbackToBasicSchema) {
            log.error({
                message: 'Error rendering form, falling back to basic schema',
                error,
                data: this.#orderData.value,
            });
        } else if (formRenderFailureCount + 1 < MAX_FORM_RENDER_RETRIES) {
            log.error({
                message: `Error rendering form on fallback schema, retrying render (attempt ${formRenderFailureCount + 1} of ${MAX_FORM_RENDER_RETRIES})`,
                error,
                data: this.#orderData.value,
            });
        } else {
            log.error({
                message: `Error rendering form, giving up after ${formRenderFailureCount + 1} failures`,
                error,
                data: this.#orderData.value,
            });
        }

        this.dispatch(formRenderFailed());
    }

    dispatch(action: TradeProAction) {
        const currentState = this.#state.value;
        this.#state.value = tradeProReducer(currentState, action);
    }

    private get widget(): TradeProWidgetModelContract {
        return this.widgetData.widget as TradeProWidgetModelContract;
    }

    get marketDataTable() {
        return this.#marketDataTable;
    }

    #marketData: ReadonlySignal<MarketData> = computed(() => this.#state.value.marketData);
    get marketData() {
        return this.#marketData;
    }

    get underlyingSymbol() {
        return this.#underlyingSymbol;
    }

    #instrument = computed<Instrument>(() => {
        return this.#orderData.value.instrument || { ...this.#defaultInstrument };
    });
    get instrument() {
        return this.#instrument;
    }

    setInstrument(instrument: Instrument) {
        // set to redux, the redux subscriber in init() will then set to the instrument in the TradeProState
        this.store.dispatch(updateTradeProInstrument(this.widgetData.tabId, instrument));
    }

    #showTradeToast = computed(() => this.#state.value.showTradeToast);
    get showTradeToast() {
        return this.#showTradeToast;
    }

    setShowTradeToast(showTradeToast: boolean) {
        this.dispatch(setShowTradeToast(showTradeToast));
        this.update({ showTradeToast });
    }

    #skipTradeConfirmation = computed(() => this.#state.value.skipTradeConfirmation);
    get skipTradeConfirmation() {
        return this.#skipTradeConfirmation;
    }

    setSkipTradeConfirmation(skipTradeConfirmation: boolean) {
        this.dispatch(setSkipTradeConfirmation(skipTradeConfirmation));
        this.update({ skipTradeConfirmation });
    }

    get orderData() {
        return this.#orderData;
    }

    setOrderData(newOrderData: Partial<OrderData>, options?: { fromStoreSync?: boolean }) {
        this.dispatch(patchOrderData(newOrderData));

        // Only dispatch linking actions for user-initiated changes, not store syncs
        if (!options?.fromStoreSync) {
            const newAccount = this.#orderData.value.account;
            if (newAccount && newAccount !== this.widget.accountId) {
                this.store.dispatch(userSelectedTradeProAccount(this.widgetData.tabId, newAccount));
            }
        }
    }

    #orderErrors: ReadonlySignal<ErrorObject[]> = computed(() => this.#state.value.orderErrors || []);
    get orderErrors() {
        return this.#orderErrors;
    }

    setOrderErrors(errors: ErrorObject[]) {
        this.dispatch(setOrderErrors(errors));
    }

    setPriceByClickingATicker(value: number | undefined, ticker: PriceTicker) {
        this.dispatch(setPriceByClickingATicker(value, ticker));
    }

    #executorsSchema: ReadonlySignal<Record<string, any>> = computed(() => this.#state.value.executorsSchema);
    get executorsSchema(): ReadonlySignal<Record<string, any>> {
        return this.#executorsSchema;
    }

    #executorsSchemaIsLoading: ReadonlySignal<boolean> = computed(() => this.#state.value.executorsSchemaIsLoading);
    get executorsSchemaIsLoading(): ReadonlySignal<boolean> {
        return this.#executorsSchemaIsLoading;
    }

    #executorsSchemaFetchingError: ReadonlySignal<Error | null> = computed(
        () => this.#state.value.executorsSchemaFetchingError,
    );
    get executorsSchemaFetchingError(): ReadonlySignal<Error | null> {
        return this.#executorsSchemaFetchingError;
    }

    #exDestinationsWithRoutes: ReadonlySignal<Record<string, string[]>> = computed(
        () => this.#state.value.exDestinationsWithRoutes,
    );
    get exDestinationsWithRoutes(): ReadonlySignal<Record<string, string[]>> {
        return this.#exDestinationsWithRoutes;
    }

    #schema: ReadonlySignal<JsonSchema> = computed(() => this.#state.value.schema);
    get schema(): ReadonlySignal<JsonSchema> {
        return this.#schema;
    }

    #uischema: ReadonlySignal<Layout> = computed(() => this.#state.value.uischema);
    get uischema() {
        return this.#uischema;
    }

    #allowedInstrumentTypes: ReadonlySignal<InstrumentType[]> = computed(() => {
        // Multi-leg widgets only support equity instruments
        if (this.isMultiLegWidget) {
            return ['equity'];
        }
        const types = this.#state.value.allowedInstrumentTypes;
        return types?.length ? types : (['equity'] as InstrumentType[]);
    });
    get allowedInstrumentTypes(): ReadonlySignal<InstrumentType[]> {
        return this.#allowedInstrumentTypes;
    }

    #isMultiLegState = computed(() => isMultiLegState(this.#state.value));
    #DEFAULT_SIDES = [OrderAction.Buy, OrderAction.Sell, OrderAction.SellShort];
    #allowedSides: ReadonlySignal<OrderAction[]> = computed((): OrderAction[] => {
        if (this.#isMultiLegState.value) {
            return [OrderAction.MultiLeg];
        }

        try {
            const sidesArray = extractSchemaPropertyOptions<OrderAction>(this.#schema.value, 'properties.side');

            if (!sidesArray) {
                const currentAllowedSides = this.#allowedSides.peek();
                if (currentAllowedSides !== this.#DEFAULT_SIDES) {
                    log.error('No sides defined in schema, defaulting to buy/sell/sell-short');
                }
                return this.#DEFAULT_SIDES;
            }

            return sidesArray;
        } catch (e) {
            log.error('Error parsing allowed sides from schema, defaulting to buy/sell/sell-short', { error: e });
            return this.#DEFAULT_SIDES;
        }
    });
    get allowedSides(): ReadonlySignal<OrderAction[]> {
        return this.#allowedSides;
    }

    handleCsvUpload(_conversionResult: ConversionResult): void {
        // CSV upload handling currently lives in the widget header component.
    }

    private update(
        updates: Partial<{
            symbol: string;
            accountId: string | null;
            showTradeToast: boolean;
            skipTradeConfirmation: boolean;
        }>,
    ) {
        this.store.dispatch(
            updateWidget({
                tabId: this.widgetData.tabId,
                widgetUpdates: {
                    ...updates,
                },
            }),
        );
    }

    #trackOrderValidationFailed(reason: string): void {
        try {
            container.getOptional('AnalyticsService')?.track({ type: 'order_validation_failed', reason });
        } catch {
            // Analytics should never break the app
        }
    }

    #trackOrderSubmitted(side: string, orderType: string, symbol: string, isMultiLeg: boolean): void {
        try {
            container.getOptional('AnalyticsService')?.track({
                type: 'order_submitted',
                side,
                orderType,
                symbol,
                isMultiLeg,
            });
        } catch {
            // Analytics should never break the app
        }
    }

    async sendOrder(side: OrderAction): Promise<Order | false> {
        if (this.orderErrors.value.length) {
            log.warn({ message: 'errors', errors: this.orderErrors.value });
            this.#trackOrderValidationFailed('order_has_errors');
            return false;
        }

        // Handle multi-leg orders differently
        if (this.isMultiLegWidget) {
            // Validate multi-leg order
            if (this.legs.value.length < 1) {
                log.warn('Multi-leg order requires at least 1 leg');
                this.#trackOrderValidationFailed('multi_leg_requires_at_least_1_leg');
                return false;
            }

            // Validate all legs have instruments
            const invalidLegs = this.legs.value.filter((leg) => !leg.symbolPickerState.symbolString);
            if (invalidLegs.length > 0) {
                log.warn('All legs must have valid instruments');
                this.#trackOrderValidationFailed('multi_leg_invalid_instruments');
                return false;
            }

            const instrument = this.#orderData.value.instrument;
            if (!instrument) {
                log.warn('No instrument selected for order');
                this.#trackOrderValidationFailed('no_instrument_selected');
                return false;
            }

            let type;
            try {
                type = OrderType.parse(this.#orderData.value.type);
            } catch (_e) {
                log.warn('Invalid order type');
                this.#trackOrderValidationFailed('invalid_order_type');
                return false;
            }

            // Transform legs to API format - only include known properties
            const legs = this.legs.value
                .filter((leg) => leg.instrument !== undefined)
                .map((leg) => {
                    const legData: {
                        instrument: InstrumentFormState;
                        side: OrderAction;
                        quantityRatio: number;
                        openClose?: OpenClose;
                    } = {
                        instrument: leg.instrument!,
                        side: leg.side,
                        quantityRatio: leg.quantityRatio,
                    };

                    // Only include openClose for option legs
                    if (leg instanceof OptionLeg) {
                        legData.openClose = leg.openClose;
                    }

                    return legData;
                });

            let data: SendOrderData = {
                ...this.orderData.value,
                type,
                instrument: parseInstrumentToFormState(instrument),
                side: OrderAction.MultiLeg,
                legs,
            };

            data = this.stripDefaultRouting(data);
            data = removeQueuedPropsFromOrderData(data);

            const result = await this.tradingSocketService.createOrder(data);
            if (result) {
                this.#trackOrderSubmitted(OrderAction.MultiLeg, type, instrumentToString(instrument), true);
            }
            return result;
        }

        // Single-leg order handling
        const instrument = this.#orderData.value.instrument;
        if (!instrument) {
            log.warn('No instrument selected for order');
            this.#trackOrderValidationFailed('no_instrument_selected');
            return false;
        }

        let type;
        try {
            type = OrderType.parse(this.#orderData.value.type);
        } catch (_e) {
            log.warn('Invalid order type');
            this.#trackOrderValidationFailed('invalid_order_type');
            return false;
        }

        let data: SendOrderData = {
            ...this.orderData.value,
            type,
            instrument: parseInstrumentToFormState(instrumentToString(instrument)),
            side,
        };

        data = this.stripDefaultRouting(data);
        data = removeQueuedPropsFromOrderData(data);

        const result = await this.tradingSocketService.createOrder(data);
        if (result) {
            this.#trackOrderSubmitted(side, type, instrumentToString(instrument), false);
        }
        return result;
    }

    private stripDefaultRouting<T extends Record<string, unknown>>(data: T): T {
        let result = data;
        if ((result as any).exDestination === DEFAULT_EXDESTINATION) {
            const { exDestination: _x, route: _r, ...rest } = result as any;
            result = rest as T;
        }
        if ((result as any).route === DEFAULT_ROUTE) {
            const { route: _r, ...rest } = result as any;
            result = rest as T;
        }
        return result;
    }

    private updateMarketDataTable(symbol: string) {
        if (symbol) {
            this.#marketDataTable.updateQueryOptions(getTickersForSymbolQuery(symbol));
        } else {
            this.#marketDataTable.updateQueryOptions(getTickersForSymbolQuery());
        }
    }

    // Multi-leg specific accessors
    get legs(): ReadonlySignal<Leg[]> {
        return this.#legs;
    }

    get activeStrategy(): ReadonlySignal<string | undefined> {
        return this.#activeStrategy;
    }

    get multiLegMarketData(): MultiLegMarketData | null {
        return this.#multiLegMarketData;
    }

    // Multi-leg specific methods
    addLeg(type: InstrumentType) {
        if (!isMultiLegState(this.#state.value)) {
            log.error('Cannot add leg to single-leg state');
            return;
        }

        // For option legs, use the underlying symbol from the widget
        const underlyingSymbol = type === 'options' ? this.underlyingSymbol.value : '';
        const instrumentTypes: InstrumentType[] = type === 'options' ? ['options'] : ['equity'];

        const leg = createLeg({
            id: crypto.randomUUID(),
            symbolPickerState: createSymbolPickerInitialState(underlyingSymbol, instrumentTypes),
            side: OrderAction.Buy,
            quantity: 1,
            quantityRatio: type === 'equity' ? 100 : 1,
        });

        this.dispatch(addLeg(leg, type));
    }

    updateLeg(legId: string, updates: Partial<Leg>) {
        if (!isMultiLegState(this.#state.value)) {
            log.error('Cannot update leg on single-leg state');
            return;
        }
        this.dispatch(updateLeg(legId, updates));
    }

    removeLeg(legId: string) {
        if (!isMultiLegState(this.#state.value)) {
            log.error('Cannot remove leg from single-leg state');
            return;
        }
        this.dispatch(removeLeg(legId));
    }

    setActiveStrategy(strategy: string) {
        if (!isMultiLegState(this.#state.value)) {
            log.error('Cannot set strategy on single-leg state');
            return;
        }
        this.dispatch(setActiveStrategy(strategy));
        // TODO: Apply strategy template
        // const legs = this.generateLegsForStrategy(strategy);
        // this.dispatch(setLegs(legs));
    }

    // Update symbol picker for a specific leg
    updateLegSymbolPicker(legId: string, symbolPickerAction: SymbolPickerAction) {
        if (!isMultiLegState(this.#state.value)) {
            log.error('Cannot update leg symbol picker on single-leg state');
            return;
        }

        this.dispatch(updateLegSymbolPicker(legId, symbolPickerAction));
    }
}
